import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { format } from "date-fns"
import {
  AlertTriangle,
  LayoutDashboard,
  RefreshCw,
  Sailboat,
  Ship,
  Users,
  Utensils,
} from "lucide-react"
import { useCallback } from "react"
import { z } from "zod"

import { DashboardService } from "@/client"
import { CalendarFreshnessIndicator } from "@/components/Calendar/CalendarFreshnessIndicator.tsx"
import { OfflineBanner } from "@/components/Calendar/OfflineBanner.tsx"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useOnlineStatus } from "@/hooks/useOnlineStatus"
import { usePullToRefresh } from "@/hooks/usePullToRefresh"
import { parseLocalDate } from "@/lib/utils"

const searchParamsSchema = z.object({
  date: z.string().optional(),
})

export const Route = createFileRoute("/_layout/dashboard")({
  component: DashboardPage,
  validateSearch: searchParamsSchema,
  staticData: {
    pageTitle: "Dashboard",
    pageIcon: LayoutDashboard,
    pageIconClassName: "text-primary",
  },
  head: () => ({
    meta: [{ title: "Dashboard - Broken Islands Booking" }],
  }),
})

interface DashboardSummary {
  guests_tonight: number
  water_taxi_passengers: number
  kayaks_out: number
  kayaks_total: number
}

interface TomorrowPreview {
  guests: number
  water_taxi_passengers: number
  kayaks_out: number
}

interface AttentionItem {
  type: string
  severity: string
  message: string
  link_to: string
}

interface DashboardData {
  date: string
  summary: DashboardSummary
  tomorrow_preview: TomorrowPreview
  attention_items: AttentionItem[]
  last_updated: string
}

function SummaryCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <Skeleton className="h-9 w-9 rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-10" />
        </div>
      </CardContent>
    </Card>
  )
}

function DashboardPage() {
  const searchParams = Route.useSearch()
  const { isOnline } = useOnlineStatus()

  const selectedDate = searchParams.date
    ? parseLocalDate(searchParams.date)
    : new Date()
  const dateString = format(selectedDate, "yyyy-MM-dd")

  const {
    data: response,
    isLoading,
    isError,
    refetch,
    dataUpdatedAt,
    isRefetching,
  } = useQuery({
    queryKey: ["dashboard", "summary", dateString],
    queryFn: () =>
      DashboardService.getDashboardSummary({
        requestedDate: dateString,
      }),
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  })

  const pullHandlers = usePullToRefresh({
    onRefresh: async () => {
      await refetch()
    },
    threshold: 60,
    enabled: isOnline,
  })

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchStart(e)
    },
    [pullHandlers],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchMove(e)
    },
    [pullHandlers],
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchEnd(e)
    },
    [pullHandlers],
  )

  const dashboard = response?.data as DashboardData | undefined

  return (
    <div
      ref={pullHandlers.containerRef}
      className="container py-6 touch-pan-y overscroll-y-contain"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {!isOnline && <OfflineBanner className="mb-4" />}

      {pullHandlers.isPulling && (
        <div
          className="flex justify-center py-2 transition-transform"
          style={{
            transform: `translateY(${Math.min(pullHandlers.pullDistance, 60)}px)`,
          }}
        >
          <RefreshCw
            className={`h-5 w-5 text-muted-foreground ${
              pullHandlers.pullDistance >= 60 ? "animate-spin" : ""
            }`}
          />
        </div>
      )}

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
          </div>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : isError ? (
        <Card className="border-destructive">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <RefreshCw className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-medium mb-2">
              Unable to load dashboard
            </h3>
            <p className="text-muted-foreground mb-4">
              There was a problem loading the dashboard data.
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : dashboard ? (
        <>
          {/* Freshness indicator */}
          {dataUpdatedAt > 0 && (
            <div className="mb-4">
              <CalendarFreshnessIndicator
                dataUpdatedAt={dataUpdatedAt}
                onRefresh={() => refetch()}
                isRefetching={isRefetching}
              />
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Guests Tonight
                  </p>
                  <p className="text-2xl font-semibold">
                    {dashboard.summary.guests_tonight}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="bg-blue-500/10 p-2 rounded-lg">
                  <Ship className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Taxi Passengers
                  </p>
                  <p className="text-2xl font-semibold">
                    {dashboard.summary.water_taxi_passengers}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="bg-green-500/10 p-2 rounded-lg">
                  <Sailboat className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Kayaks Out</p>
                  <p className="text-2xl font-semibold">
                    {dashboard.summary.kayaks_out}
                    <span className="text-sm font-normal text-muted-foreground">
                      /{dashboard.summary.kayaks_total}
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Attention Items */}
          {dashboard.attention_items.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Needs Attention
              </h2>
              <div className="space-y-2">
                {dashboard.attention_items.map((item, index) => {
                  const url = new URL(item.link_to, "http://localhost")
                  const path = url.pathname
                  const search = Object.fromEntries(url.searchParams.entries())
                  return (
                    <Link
                      key={`${item.type}-${index}`}
                      to={path}
                      search={search}
                      className="block"
                    >
                      <Card
                        className={
                          item.severity === "critical"
                            ? "border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20"
                            : "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20"
                        }
                      >
                        <CardContent className="p-3 flex items-center gap-3">
                          <Badge
                            variant={
                              item.severity === "critical"
                                ? "destructive"
                                : "secondary"
                            }
                            className="text-xs shrink-0"
                          >
                            {item.severity}
                          </Badge>
                          <span className="text-sm">{item.message}</span>
                        </CardContent>
                      </Card>
                    </Link>
                  )
                })}
              </div>
            </div>
          )}

          {/* Tomorrow Preview */}
          <div className="mb-6">
            <h2 className="text-lg font-medium mb-3">Tomorrow</h2>
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Guests</p>
                    <p className="text-xl font-semibold">
                      {dashboard.tomorrow_preview.guests}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Taxi Passengers
                    </p>
                    <p className="text-xl font-semibold">
                      {dashboard.tomorrow_preview.water_taxi_passengers}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Kayaks Out</p>
                    <p className="text-xl font-semibold">
                      {dashboard.tomorrow_preview.kayaks_out}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="text-lg font-medium mb-3">Quick Links</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Button variant="outline" className="h-auto py-3" asChild>
                <Link to="/calendar/taxi">
                  <Ship className="h-4 w-4 mr-2" />
                  Water Taxi
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-3" asChild>
                <Link to="/calendar/kitchen">
                  <Utensils className="h-4 w-4 mr-2" />
                  Kitchen
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-3" asChild>
                <Link to="/calendar/kayak">
                  <Sailboat className="h-4 w-4 mr-2" />
                  Kayaks
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-3" asChild>
                <Link to="/admin">
                  <Users className="h-4 w-4 mr-2" />
                  Users
                </Link>
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
